/**
 * MenuDayCard component
 * Displays a day's lunch and dinner meals in the weekly menu
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { formatDayNameAndNumber } from '@/lib/utils/date';
import { fr } from 'date-fns/locale';
import { format } from 'date-fns';
import type { WeekDay } from '@/types/api';

interface MenuDayCardProps {
  dayData: WeekDay;
  isToday?: boolean;
  isPast?: boolean;
}

/**
 * MenuDayCard component
 *
 * Features:
 * - Display day name and date
 * - Show lunch and dinner meals
 * - Link to meal details
 * - Empty state for unset meals
 * - Highlight today
 *
 * @example
 * ```tsx
 * <MenuDayCard
 *   dayData={{
 *     date: '2025-01-06',
 *     lunch: { meal: { name: 'Pasta' }, ... },
 *     dinner: { meal: { name: 'Salad' }, ... },
 *   }}
 *   isToday={true}
 * />
 * ```
 */
export function MenuDayCard({ dayData, isToday, isPast: _isPast }: MenuDayCardProps) {
  const { t } = useTranslation();
  const date = new Date(dayData.date);
  const { dayName, dayNumber } = formatDayNameAndNumber(date);
  const monthName = format(date, 'MMM', { locale: fr });

  const hasMeals = dayData.lunch || dayData.dinner;

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-4 ${
        isToday ? 'ring-2 ring-primary-500' : ''
      }`}
    >
      {/* Day header */}
      <div className="text-center mb-4 pb-3 border-b border-gray-200">
        <div className="text-sm font-medium text-gray-600">{dayName}</div>
        <div
          className={`text-2xl font-bold mt-1 ${
            isToday ? 'text-primary-600' : 'text-gray-900'
          }`}
        >
          {dayNumber}
        </div>
        <div className="text-xs text-gray-500">{monthName}</div>
      </div>

      {/* Meals */}
      <div className="space-y-4">
        {/* Lunch */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {t('availability.lunch')}
          </h3>
          {dayData.lunch ? (
            <Link
              to="/meals/$mealId"
              params={{ mealId: dayData.lunch.meal.id.toString() }}
              className="block text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors"
            >
              {dayData.lunch.meal.name}
            </Link>
          ) : (
            <p className="text-sm text-gray-400 italic">{t('menu.notSet')}</p>
          )}
        </div>

        {/* Dinner */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            {t('availability.dinner')}
          </h3>
          {dayData.dinner ? (
            <Link
              to="/meals/$mealId"
              params={{ mealId: dayData.dinner.meal.id.toString() }}
              className="block text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors"
            >
              {dayData.dinner.meal.name}
            </Link>
          ) : (
            <p className="text-sm text-gray-400 italic">{t('menu.notSet')}</p>
          )}
        </div>
      </div>

      {/* Empty state indicator */}
      {!hasMeals && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">{t('menu.noMealsPlanned')}</p>
        </div>
      )}
    </div>
  );
}
